import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select } from "../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Textarea } from "../../../components/ui/textarea";
import type { UnifiedFolder } from "../hooks/use-prompt-source";
import { useCloudPromptsStore } from "../store/cloud-prompts";
import type { CloudPromptVersion } from "../types/cloud";
import AddVariablePopover from "./AddVariablePopover";
import VersionDetail from "./VersionDetail";
import VersionList from "./VersionList";
import type { PromptEditorState } from "./prompts-view-types";

type PromptEditorView = "edit" | "history" | "version-detail";

interface PromptEditorDialogProps {
  state: PromptEditorState;
  isCloudSource: boolean;
  folders: UnifiedFolder[];
  onClose: () => void;
  onSave: (next: PromptEditorState) => Promise<void>;
}

export default function PromptEditorDialog({
  state,
  isCloudSource,
  folders,
  onClose,
  onSave,
}: Readonly<PromptEditorDialogProps>): JSX.Element {
  const [draft, setDraft] = useState(state);
  const [view, setView] = useState<PromptEditorView>("edit");
  const [versions, setVersions] = useState<CloudPromptVersion[]>([]);
  const [selectedVersion, setSelectedVersion] =
    useState<CloudPromptVersion | null>(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [isVariablePopoverOpen, setIsVariablePopoverOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number | null>(null);
  const getPromptVersions = useCloudPromptsStore(
    (store) => store.getPromptVersions,
  );
  const restorePromptVersion = useCloudPromptsStore(
    (store) => store.restorePromptVersion,
  );

  const handleTextareaSelect = useCallback(() => {
    if (textareaRef.current) {
      cursorPositionRef.current = textareaRef.current.selectionStart;
    }
  }, []);

  const handleAddVariable = useCallback((syntax: string) => {
    setDraft((previous) => {
      const { content } = previous;
      const position = cursorPositionRef.current ?? content.length;
      const before = content.slice(0, position);
      const after = content.slice(position);

      const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
      const needsSpaceAfter = after.length > 0 && !/^\s/.test(after);

      const inserted = `${needsSpaceBefore ? " " : ""}${syntax}${needsSpaceAfter ? " " : ""}`;
      const newContent = before + inserted + after;

      const newCursorPosition = before.length + inserted.length;
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(
            newCursorPosition,
            newCursorPosition,
          );
        }
      });

      return { ...previous, content: newContent };
    });
    setIsVariablePopoverOpen(false);
  }, []);

  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => a.name.localeCompare(b.name)),
    [folders],
  );

  const canShowHistoryTab = Boolean(
    isCloudSource && draft.mode === "edit" && draft.prompt,
  );

  useEffect(() => {
    setDraft(state);
    setView("edit");
    setVersions([]);
    setSelectedVersion(null);
    setIsLoadingVersions(false);
    setVersionsError(null);
    setIsVariablePopoverOpen(false);
    cursorPositionRef.current = null;
  }, [state]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSave(draft);
  };

  const fetchVersions = async () => {
    if (!draft.prompt) {
      return;
    }

    setIsLoadingVersions(true);
    setVersionsError(null);

    const nextVersions = await getPromptVersions(draft.prompt.id);
    if (nextVersions) {
      setVersions(nextVersions);
    } else {
      setVersionsError(
        useCloudPromptsStore.getState().error ?? "Failed to load versions.",
      );
    }

    setIsLoadingVersions(false);
  };

  const handleViewVersion = (version: CloudPromptVersion) => {
    setSelectedVersion(version);
    setView("version-detail");
  };

  const handleBackToHistory = () => {
    setSelectedVersion(null);
    setView("history");
  };

  const handleRestoreVersion = async (versionId: string): Promise<boolean> => {
    if (!draft.prompt) {
      return false;
    }

    const restoredPrompt = await restorePromptVersion(
      draft.prompt.id,
      versionId,
    );
    if (!restoredPrompt) {
      setVersionsError(
        useCloudPromptsStore.getState().error ?? "Failed to restore version.",
      );
      return false;
    }

    setDraft((previous) => ({
      ...previous,
      title: restoredPrompt.title,
      content: restoredPrompt.content,
      folderId: restoredPrompt.folderId,
    }));

    setVersions((previous) => {
      const currentVersion: CloudPromptVersion = {
        id: restoredPrompt.currentVersionId,
        promptId: restoredPrompt.id,
        title: restoredPrompt.title,
        content: restoredPrompt.content,
        editedBy: restoredPrompt.editedBy,
        editedByName: restoredPrompt.editedByName,
        createdAt: restoredPrompt.versionCreatedAt,
        isCurrent: true,
      };

      return [
        currentVersion,
        ...previous
          .filter((version) => version.id !== currentVersion.id)
          .map((version) => ({ ...version, isCurrent: false })),
      ];
    });

    setSelectedVersion(null);
    setView("edit");
    setVersionsError(null);
    return true;
  };

  const handleTabChange = (nextTab: string) => {
    if (nextTab === "edit") {
      setSelectedVersion(null);
      setView("edit");
      return;
    }

    if (nextTab === "history" && canShowHistoryTab) {
      setSelectedVersion(null);
      setView("history");
      void fetchVersions();
    }
  };

  const tabValue = view === "edit" ? "edit" : "history";

  return (
    <Dialog open>
      <DialogOverlay onClick={onClose} />
      <DialogContent role="dialog" aria-modal="true" aria-label="Prompt editor">
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {draft.mode === "create" ? "Create Prompt" : "Edit Prompt"}
            </DialogTitle>
          </DialogHeader>

          {canShowHistoryTab ? (
            <Tabs value={tabValue} onValueChange={handleTabChange}>
              <TabsList className="h-auto w-full justify-start gap-2 border-b border-border/70 pb-2">
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}

          {view === "edit" ? (
            <>
              <div className="flex flex-col gap-[0.35rem]">
                <Label htmlFor="prompt-editor-title">Title</Label>
                <Input
                  id="prompt-editor-title"
                  value={draft.title}
                  onChange={(event) =>
                    setDraft((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }))
                  }
                  maxLength={200}
                  autoFocus
                />
              </div>

              <div className="flex flex-col gap-[0.35rem]">
                <Label htmlFor="prompt-editor-folder">Folder</Label>
                <Select
                  id="prompt-editor-folder"
                  value={draft.folderId ?? ""}
                  onChange={(event) =>
                    setDraft((previous) => ({
                      ...previous,
                      folderId: event.target.value ? event.target.value : null,
                    }))
                  }
                >
                  <option value="">Unfiled</option>
                  {sortedFolders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {folder.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-col gap-[0.35rem]">
                <Label htmlFor="prompt-editor-content">Content</Label>
                <Textarea
                  ref={textareaRef}
                  id="prompt-editor-content"
                  value={draft.content}
                  onChange={(event) => {
                    setDraft((previous) => ({
                      ...previous,
                      content: event.target.value,
                    }));
                    cursorPositionRef.current = event.target.selectionStart;
                  }}
                  onSelect={handleTextareaSelect}
                  onKeyUp={handleTextareaSelect}
                  onClick={handleTextareaSelect}
                  maxLength={30000}
                  rows={8}
                />
                <div className="relative flex items-center justify-between">
                  <div className="relative">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-0 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setIsVariablePopoverOpen((open) => !open)}
                    >
                      + Variable
                    </Button>
                    {isVariablePopoverOpen ? (
                      <AddVariablePopover
                        content={draft.content}
                        onAdd={handleAddVariable}
                        onClose={() => setIsVariablePopoverOpen(false)}
                      />
                    ) : null}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {"Use {{name}} for variables"}
                  </p>
                </div>
              </div>
            </>
          ) : null}

          {view === "history" ? (
            <VersionList
              versions={versions}
              isLoading={isLoadingVersions}
              error={versionsError}
              onViewVersion={handleViewVersion}
              onRetry={fetchVersions}
            />
          ) : null}

          {view === "version-detail" && selectedVersion ? (
            <VersionDetail
              version={selectedVersion}
              onBack={handleBackToHistory}
              onRestore={handleRestoreVersion}
            />
          ) : null}

          {view === "edit" ? (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!draft.title.trim() || !draft.content.trim()}
              >
                Save
              </Button>
            </DialogFooter>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
